<template>
  <div class="generic-order-details">
    <div v-if="detailEntries.length > 0">
      <div v-if="simpleEntries.length > 0" class="row g-2 g-lg-3 mb-3">
        <div
          v-for="[key, value] in simpleEntries"
          :key="key"
          class="col-md-6 col-xl-4"
        >
          <div class="detail-item h-100">
            <div class="text-muted small mb-1">
              {{ formatKey(key) }}
            </div>
            <div class="fw-semibold text-break">
              {{ formatValue(value) }}
            </div>
          </div>
        </div>
      </div>

      <div
        v-for="[key, value] in complexEntries"
        :key="key"
        class="details-panel mb-3"
      >
        <div class="text-uppercase text-muted fw-semibold mb-3 section-label">
          {{ formatKey(key) }}
        </div>

        <div v-if="Array.isArray(value)" class="d-grid gap-2">
          <div v-for="(item, index) in value" :key="index" class="nested-card">
            <div class="text-muted small mb-2 fw-semibold">
              {{ formatKey(key) }} {{ index + 1 }}
            </div>

            <div v-if="item && typeof item === 'object'" class="row g-2">
              <div
                v-for="[subKey, subValue] in objectEntries(item)"
                :key="subKey"
                class="col-md-6"
              >
                <div class="nested-detail-item h-100">
                  <div class="text-muted small mb-1">
                    {{ formatKey(subKey) }}
                  </div>
                  <div class="small text-break">
                    {{ formatValue(subValue) }}
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="small">
              {{ formatValue(item) }}
            </div>
          </div>
        </div>

        <div v-else class="row g-2">
          <div
            v-for="[subKey, subValue] in objectEntries(value)"
            :key="subKey"
            class="col-md-6"
          >
            <div class="nested-detail-item h-100">
              <div class="text-muted small mb-1">
                {{ formatKey(subKey) }}
              </div>
              <div class="small text-break">
                {{ formatValue(subValue) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="text-center py-5 text-muted">
      <FontAwesomeIcon icon="info-circle" size="2x" class="mb-3 opacity-50" />
      <p class="mb-0">
        {{ $t("company_orders.generic.no_additional_details") }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatDateTimeIntoDate } from "@/composables/formatUtils";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

// Initialize translation function
const { t } = useI18n();

interface Props {
  details: Record<string, any> | null | undefined;
}

const props = defineProps<Props>();

type DetailEntry = [string, unknown];

/**
 * Format object key for display (convert snake_case to Title Case)
 */
const formatKey = (key: string): string => {
  return key
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/**
 * Check if a value is simple (string, number, boolean, null)
 */
const isSimpleValue = (value: unknown): boolean => {
  return (
    value === null ||
    value === undefined ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  );
};

/**
 * Format value for display
 */
const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return t("common.none");
  }

  if (typeof value === "boolean") {
    return value ? t("common.yes") : t("common.no");
  }

  if (typeof value === "string") {
    // Detect ISO-like date strings (YYYY-MM-DD or YYYY-MM-DDTHH:MM:SS[Z])
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
      const formatted = formatDateTimeIntoDate(value);
      return formatted || value; // Fallback to original if formatting fails
    }

    return value;
  }

  return String(value);
};

const objectEntries = (value: unknown): DetailEntry[] => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return [];
  }

  return Object.entries(value as Record<string, unknown>);
};

const detailEntries = computed<DetailEntry[]>(() => {
  return Object.entries(props.details ?? {});
});

const simpleEntries = computed<DetailEntry[]>(() => {
  return detailEntries.value.filter(([, value]) => isSimpleValue(value));
});

const complexEntries = computed<DetailEntry[]>(() => {
  return detailEntries.value.filter(([, value]) => !isSimpleValue(value));
});
</script>

<style scoped lang="scss">
@use "../order-details-shared";

.detail-item {
  border: 1px solid var(--phoenix-secondary-border-subtle);
}

.nested-card {
  padding: 0.875rem;
  border: 1px solid var(--phoenix-secondary-border-subtle);
  border-radius: 0.75rem;
  background: var(--phoenix-secondary-bg-subtle);
}

.nested-detail-item {
  padding: 0.625rem 0.75rem;
  border-radius: 0.625rem;
  background: rgba(var(--phoenix-body-bg-rgb), 0.85);
}
</style>
